import { useState } from "react";
import { Star, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Single star-rating row (1-5)
const StarRow = ({ label, value, onChange, testPrefix }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 border-b border-slate-100 last:border-b-0">
    <span className="text-sm text-slate-700">{label}</span>
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="p-1 rounded-md hover:bg-amber-50 transition-colors"
          aria-label={`${n} de 5`}
          data-testid={`${testPrefix}-${n}`}
        >
          <Star
            className={`h-7 w-7 transition-colors ${
              n <= value ? "text-amber-400 fill-amber-400" : "text-slate-300"
            }`}
          />
        </button>
      ))}
    </div>
  </div>
);

/**
 * Post-response checkout survey shown on the public consult status page.
 * 3 star dimensions + optional NPS 0-10 + optional comment.
 * Excluded for recetas (backend enforces it too).
 */
const PatientSurveyCard = ({ consultId, accessToken, escalated = false, alreadySubmitted = false }) => {
  const [ratingResponse, setRatingResponse] = useState(0);
  const [ratingTime, setRatingTime] = useState(0);
  const [ratingEase, setRatingEase] = useState(0);
  const [nps, setNps] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(alreadySubmitted);

  if (submitted) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center" data-testid="survey-thanks">
        <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
        <p className="font-semibold text-emerald-900">¡Gracias por tu opinión!</p>
        <p className="text-sm text-emerald-800 mt-1">
          Tu respuesta nos ayuda a seguir mejorando el servicio.
        </p>
      </div>
    );
  }

  const allRated = ratingResponse > 0 && ratingTime > 0 && ratingEase > 0;

  const handleSubmit = async () => {
    if (!allRated) {
      toast.error("Por favor, completa las tres calificaciones con estrellas");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/consults/${consultId}/survey?t=${accessToken}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rating_response: ratingResponse,
          rating_time: ratingTime,
          rating_ease: ratingEase,
          nps: nps,
          comment: comment.trim() || null,
        }),
      });
      if (response.ok) {
        setSubmitted(true);
        toast.success("¡Gracias! Tu opinión fue registrada");
      } else if (response.status === 409) {
        setSubmitted(true);
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || "No pudimos registrar tu opinión. Intenta de nuevo.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error al conectar con el servidor");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-blue-200 p-6" data-testid="survey-card" id="encuesta">
      <h3 className="font-semibold text-slate-900 mb-1">¿Cómo fue tu experiencia?</h3>
      <p className="text-sm text-slate-500 mb-4">
        Tu opinión es anónima para el profesional y nos ayuda a mejorar el servicio. Te toma menos de 1 minuto.
      </p>

      <div className="mb-4">
        <StarRow
          label={escalated ? "Satisfacción con la atención recibida" : "Satisfacción con la respuesta"}
          value={ratingResponse}
          onChange={setRatingResponse}
          testPrefix="survey-stars-response"
        />
        <StarRow
          label="Tiempo de respuesta del profesional"
          value={ratingTime}
          onChange={setRatingTime}
          testPrefix="survey-stars-time"
        />
        <StarRow
          label="Facilidad de uso de la plataforma"
          value={ratingEase}
          onChange={setRatingEase}
          testPrefix="survey-stars-ease"
        />
      </div>

      {/* NPS 0-10 */}
      <div className="mb-4">
        <p className="text-sm text-slate-700 mb-2">
          ¿Qué tan probable es que recomiendes este servicio? <span className="text-slate-400">(opcional)</span>
        </p>
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: 11 }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setNps(nps === i ? null : i)}
              className={`w-9 h-9 rounded-full text-sm font-medium border transition-colors ${
                nps === i
                  ? "bg-[#1d366b] text-white border-[#1d366b]"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
              data-testid={`survey-nps-${i}`}
            >
              {i}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1 max-w-md">
          <span>Nada probable</span>
          <span>Muy probable</span>
        </div>
      </div>

      {/* Comment */}
      <div className="mb-4">
        <p className="text-sm text-slate-700 mb-2">
          ¿Algo más que quieras contarnos? <span className="text-slate-400">(opcional)</span>
        </p>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Tu comentario..."
          rows={3}
          maxLength={2000}
          className="resize-none"
          data-testid="survey-comment"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting || !allRated}
        className="w-full h-11 bg-[#1d366b] hover:bg-[#16295a]"
        data-testid="survey-submit-btn"
      >
        {submitting ? "Enviando..." : "Enviar opinión"}
        <Send className="ml-2 h-4 w-4" />
      </Button>
      {!allRated && (
        <p className="text-xs text-slate-400 text-center mt-2">
          Completa las tres calificaciones con estrellas para enviar
        </p>
      )}
    </div>
  );
};

export default PatientSurveyCard;
